import { useState } from 'react'
import { MessageSquare } from 'lucide-react'
import { useCapability } from '@/features/authorization/use-capabilities'
import { useT } from '@/internationalization/i18n'
import { FeedbackDialog } from './FeedbackDialog'

/**
 * The floating "Share feedback" button and the dialog it opens.
 *
 * Fixed to the viewport, so it takes no room in the layout and nothing moves
 * when it appears. Below `sm` it shrinks to its icon: a labelled pill over the
 * bottom corner of a 375px page covers content the person is reading.
 *
 * Gated on the DEPLOYMENT capability only. Every signed-in member may send
 * feedback, so there is no permission to check; a deployment that has not
 * applied migration 050 reports the capability false and the button is never
 * offered.
 */
export function FeedbackWidget() {
  const t = useT()
  const enabled = useCapability('feedback')
  const [isOpen, setOpen] = useState(false)

  if (!enabled) return null

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-haspopup="dialog"
        aria-label={t('feedback.open')}
        className="fixed bottom-[max(1rem,env(safe-area-inset-bottom))] right-[max(1rem,env(safe-area-inset-right))] z-sticky inline-flex h-11 min-w-[2.75rem] items-center justify-center gap-2 rounded-full bg-brand px-3 text-sm font-medium text-brand-fg shadow-md transition duration-fast hover:-translate-y-px hover:bg-brand-hover hover:shadow-lg motion-reduce:transition-none motion-reduce:hover:translate-y-0 sm:px-4 print:hidden"
      >
        <MessageSquare aria-hidden className="h-4 w-4" strokeWidth={1.75} />
        <span className="hidden sm:inline">{t('feedback.open')}</span>
      </button>
      {isOpen && <FeedbackDialog onClose={() => setOpen(false)} />}
    </>
  )
}
